/**
 * CustomerTipSelection — Dynamic tip presets + always-visible Custom Tip button
 * - Shows % presets with calculated $ amounts
 * - For higher orders ($50+), shows smart $ amounts with % label
 * - Custom Tip button opens a large input field
 * - No Tip always available
 */
import React, { useState, useRef, useEffect } from 'react';

const PCT_PRESETS = [
  { label: '15%', pct: 0.15 },
  { label: '18%', pct: 0.18 },
  { label: '20%', pct: 0.20 },
  { label: '25%', pct: 0.25 },
];

const HIGH_VALUE_THRESHOLD = 50;

function getDollarPresets(subtotal) {
  const base = [0.15, 0.18, 0.20, 0.25];
  return base.map((pct) => {
    const raw = subtotal * pct;
    const rounded = Math.ceil(raw);
    return { amount: rounded, pct: Math.round((rounded / subtotal) * 100) };
  });
}

export default function CustomerTipSelection({ subtotal, tax, tipAmount, onConfirm }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [localTip, setLocalTip] = useState(tipAmount || 0);
  const inputRef = useRef(null);

  const total = subtotal + tax + localTip;
  const fmt = (n) => `$${Number(n).toFixed(2)}`;
  const isHighValue = subtotal >= HIGH_VALUE_THRESHOLD;

  useEffect(() => {
    if (showCustomInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustomInput]);

  const selectPreset = (index, amount) => {
    setSelectedIndex(index);
    setLocalTip(amount);
    setShowCustomInput(false);
    setCustomValue('');
  };

  const handleCustomTap = () => {
    setShowCustomInput(true);
    setSelectedIndex('custom');
  };

  const applyCustom = () => {
    const val = parseFloat(customValue);
    if (!isNaN(val) && val >= 0) {
      setLocalTip(val);
      setSelectedIndex('custom');
    }
  };

  const noTip = () => {
    setLocalTip(0);
    setSelectedIndex('none');
    setShowCustomInput(false);
    setCustomValue('');
  };

  // Build preset buttons based on order value
  const renderPresets = () => {
    if (isHighValue) {
      const presets = getDollarPresets(subtotal);
      return presets.map((p, i) => {
        const active = selectedIndex === i;
        return (
          <button
            key={i}
            onClick={() => selectPreset(i, p.amount)}
            className={`cfd-tip-btn ${active ? 'active' : ''}`}
            data-testid={`tip-dollar-${p.amount}`}
          >
            <span className="cfd-tip-pct">{fmt(p.amount)}</span>
            <span className="cfd-tip-amt">{p.pct}%</span>
          </button>
        );
      });
    }

    return PCT_PRESETS.map(({ label, pct }, i) => {
      const amt = Number((subtotal * pct).toFixed(2));
      const active = selectedIndex === i;
      return (
        <button
          key={label}
          onClick={() => selectPreset(i, amt)}
          className={`cfd-tip-btn ${active ? 'active' : ''}`}
          data-testid={`tip-${label}`}
        >
          <span className="cfd-tip-pct">{label}</span>
          <span className="cfd-tip-amt">{fmt(amt)}</span>
        </button>
      );
    });
  };

  return (
    <div className="cfd-panel cfd-slide-up" data-testid="tip-screen">
      <h2 className="cfd-heading">Add a Tip</h2>
      <p className="cfd-subtext">Your generosity is appreciated!</p>

      {/* Preset grid + Custom Tip button */}
      <div className="cfd-tip-grid-wrap">
        <div className="cfd-tip-grid">
          {renderPresets()}
        </div>

        {/* Custom Tip — always visible */}
        <div className="cfd-tip-actions-row">
          <button
            onClick={handleCustomTap}
            className={`cfd-tip-custom-btn ${selectedIndex === 'custom' ? 'active' : ''}`}
            data-testid="custom-tip-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
              <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
            Custom Tip
          </button>
          <button onClick={noTip} className="cfd-tip-notip-btn" data-testid="no-tip-btn">
            No Tip
          </button>
        </div>
      </div>

      {/* Custom tip input (shown when Custom Tip is tapped) */}
      {showCustomInput && (
        <div className="cfd-custom-tip-panel cfd-slide-up" data-testid="custom-tip-panel">
          <div className="cfd-custom-tip-input-row">
            <span className="cfd-custom-tip-prefix">$</span>
            <input
              ref={inputRef}
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyCustom()}
              className="cfd-input-large"
              data-testid="custom-tip-input"
            />
          </div>
          <button onClick={applyCustom} className="cfd-btn-secondary cfd-custom-apply" data-testid="apply-tip-btn">
            Apply Tip
          </button>
        </div>
      )}

      {/* Summary */}
      <div className="cfd-summary-box">
        <div className="cfd-summary-row"><span>Subtotal + Tax</span><span>{fmt(subtotal + tax)}</span></div>
        <div className="cfd-summary-row cfd-highlight"><span>Tip</span><span>{fmt(localTip)}</span></div>
        <div className="cfd-summary-total"><span>Total</span><span>{fmt(total)}</span></div>
      </div>

      <button
        onClick={() => onConfirm(localTip)}
        className="cfd-btn-primary"
        data-testid="confirm-tip-btn"
      >
        Continue
      </button>
    </div>
  );
}
